import { useRef, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PersonIcon from "@mui/icons-material/Person";
import { useIdentity } from "../../auth/useIdentity";
import { avatarErrorText, removeOwnAvatar, setOwnAvatar } from "../../api/account";
import { shrinkPhoto } from "./avatarImage";
import { useOwnAvatar } from "./useOwnAvatar";

/**
 * Settings › Sign-in & security › Photo (owner 2026-10-26: users cannot
 * reach ZITADEL, so the photo is kept in radaccess and edited here). The
 * photo is shrunk to a 256 px square in the browser, then sent.
 */
export function PhotoCard() {
  const identity = useIdentity();
  const ns = identity.rootNamespace ?? identity.namespace ?? "";
  const qc = useQueryClient();
  const url = useOwnAvatar();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null);
  const initials = identity.firstName ? identity.firstName.slice(0, 1).toUpperCase() : null;

  async function run(action: () => Promise<string>) {
    if (busy) return;
    setBusy(true);
    try {
      const text = await action();
      await qc.invalidateQueries({ queryKey: ["contactDetails", ns] });
      setNotice({ severity: "success", text });
    } catch (err) {
      setNotice({ severity: "error", text: avatarErrorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const choose = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // the same file can be chosen again
    if (!file) return;
    void run(async () => {
      await setOwnAvatar(identity, ns, await shrinkPhoto(file));
      return "Photo saved.";
    });
  };

  const remove = () =>
    void run(async () => {
      await removeOwnAvatar(identity, ns);
      return "Photo removed.";
    });

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }} data-testid="photo-card">
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          Photo
        </Typography>
        {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Avatar src={url ?? undefined} alt="Your photo" sx={{ width: 72, height: 72, bgcolor: "primary.main", color: "primary.contrastText", fontSize: 28 }}>
            {initials ?? <PersonIcon />}
          </Avatar>
          <input ref={input} type="file" accept="image/jpeg,image/png" hidden onChange={choose} data-testid="photo-input" />
          <Button variant="outlined" onClick={() => input.current?.click()} disabled={busy}>
            {url ? "Change photo" : "Add photo"}
          </Button>
          {url && (
            <Button color="error" onClick={remove} disabled={busy}>
              Remove
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          A JPEG or PNG. It is cropped to a square and shown next to your name in the menu.
        </Typography>
      </Stack>
    </Paper>
  );
}
